import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { formatMoney } from '../lib/format';
import type { AdminUser, AuditLogEntry, MailAdminSettings, StripeAdminSettings, Transaction } from '../types';

type AdminTab = 'users' | 'stripe' | 'mail' | 'audit';

interface Props {
  currentUserId: string;
  onImpersonate: (id: string) => void;
  push: (kind: 'ok' | 'error' | 'info' | 'warn', message: string) => void;
}

/**
 * The whole admin console in one panel with an internal tab strip, rather
 * than four separate top-level tabs — it's one audience (an admin) doing
 * one connected job (run the business side of Z3NTRAFY), and this keeps
 * App.tsx's own tab bar from growing a section almost nobody sees.
 */
export function AdminPanel({ currentUserId, onImpersonate, push }: Props) {
  const [tab, setTab] = useState<AdminTab>('users');

  return (
    <div className="card">
      <div className="card-head">
        <h2>Admin</h2>
        <div className="admin-subtabs" role="tablist">
          <button role="tab" aria-selected={tab === 'users'} onClick={() => setTab('users')}>
            Users
          </button>
          <button role="tab" aria-selected={tab === 'stripe'} onClick={() => setTab('stripe')}>
            Stripe
          </button>
          <button role="tab" aria-selected={tab === 'mail'} onClick={() => setTab('mail')}>
            Mail
          </button>
          <button role="tab" aria-selected={tab === 'audit'} onClick={() => setTab('audit')}>
            Audit log
          </button>
        </div>
      </div>
      <div className="card-body">
        {tab === 'users' && <UsersTab currentUserId={currentUserId} onImpersonate={onImpersonate} push={push} />}
        {tab === 'stripe' && <StripeTab push={push} />}
        {tab === 'mail' && <MailTab push={push} />}
        {tab === 'audit' && <AuditTab />}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- users */

function UsersTab({
  currentUserId,
  onImpersonate,
  push,
}: {
  currentUserId: string;
  onImpersonate: (id: string) => void;
  push: (kind: 'ok' | 'error' | 'info' | 'warn', message: string) => void;
}) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [billingUser, setBillingUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    api
      .admin!.users()
      .then(setUsers)
      .catch((err) => push('error', err instanceof ApiError ? err.message : String(err)));
  }, [push]);

  if (!users) return <div className="empty">Loading…</div>;

  return (
    <>
      <div className="table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Joined</th>
              <th>Plan</th>
              <th>Tasks</th>
              <th>Systems</th>
              <th>Verified</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  {u.email}
                  {u.isAdmin && <span className="badge admin">admin</span>}
                </td>
                <td>{u.name ?? '—'}</td>
                <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                <td>
                  {u.subscriptionStatus ?? '—'}
                  {u.subscriptionPlan ? ` (${u.subscriptionPlan})` : ''}
                </td>
                <td>{u.taskCount}</td>
                <td>{u.connectionCount}</td>
                <td>{u.emailVerified ? 'Yes' : 'No'}</td>
                <td>
                  <div className="row-actions">
                    <button className="btn ghost sm" onClick={() => setBillingUser(u)}>
                      Billing
                    </button>
                    <button
                      className="btn ghost sm"
                      disabled={u.id === currentUserId || busyId === u.id}
                      onClick={async () => {
                        setBusyId(u.id);
                        try {
                          await onImpersonate(u.id);
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      {busyId === u.id ? <span className="spin" /> : null}
                      {u.id === currentUserId ? 'This is you' : 'View as'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {billingUser && <TransactionsModal user={billingUser} onClose={() => setBillingUser(null)} push={push} />}
    </>
  );
}

/* ------------------------------------------------------------------ billing */

function TransactionsModal({
  user,
  onClose,
  push,
}: {
  user: AdminUser;
  onClose: () => void;
  push: (kind: 'ok' | 'error' | 'info' | 'warn', message: string) => void;
}) {
  const [transactions, setTransactions] = useState<Transaction[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [amountDrafts, setAmountDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    api
      .admin!.transactions(user.id)
      .then(setTransactions)
      .catch((err) => push('error', err instanceof ApiError ? err.message : String(err)));
  }, [user.id, push]);

  async function refund(t: Transaction, amount: number | undefined, label: string) {
    if (!window.confirm(`Refund ${label} to ${user.email}? This cannot be undone.`)) return;
    setBusyId(t.id);
    try {
      const updated = await api.admin!.refund(user.id, { chargeId: t.id, amount });
      setTransactions((prev) => prev?.map((x) => (x.id === updated.id ? updated : x)) ?? prev);
      setAmountDrafts((d) => ({ ...d, [t.id]: '' }));
      push('ok', `Refunded ${label}.`);
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  function statusBadge(t: Transaction): { tone: string; label: string } {
    if (t.refunded) return { tone: 'warn', label: 'refunded' };
    if (t.status === 'succeeded') return { tone: 'ok', label: 'succeeded' };
    if (t.status === 'failed') return { tone: 'danger', label: 'failed' };
    return { tone: '', label: t.status };
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal wide" role="dialog" aria-modal="true" aria-label={`Billing history for ${user.email}`}>
        <div className="modal-head">
          <h3>Billing — {user.email}</h3>
          <div className="spacer" />
          <button type="button" className="btn ghost sm" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modal-body">
          {transactions === null ? (
            <div className="empty">Loading…</div>
          ) : transactions.length === 0 ? (
            <div className="empty">
              No charges on this account yet — it either hasn't subscribed, or is still on its free trial.
            </div>
          ) : (
            <div className="table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => {
                    const remaining = t.amount - t.amountRefunded;
                    const canRefund = t.status === 'succeeded' && !t.refunded;
                    const busy = busyId === t.id;
                    const badge = statusBadge(t);
                    const draft = amountDrafts[t.id] ?? '';
                    const draftCents = draft ? Math.round(Number(draft) * 100) : null;
                    const draftValid = draftCents != null && draftCents > 0 && draftCents <= remaining;
                    return (
                      <tr key={t.id}>
                        <td>{new Date(t.created).toLocaleDateString()}</td>
                        <td>
                          {t.description ?? '—'}
                          {t.receiptUrl && (
                            <>
                              {' '}
                              <a href={t.receiptUrl} target="_blank" rel="noreferrer">
                                receipt
                              </a>
                            </>
                          )}
                        </td>
                        <td>
                          {formatMoney(t.amount, t.currency)}
                          {t.amountRefunded > 0 && (
                            <div className="help">{formatMoney(t.amountRefunded, t.currency)} refunded</div>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${badge.tone}`}>{badge.label}</span>
                        </td>
                        <td>
                          {canRefund && (
                            <div className="refund-controls">
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                placeholder={(remaining / 100).toFixed(2)}
                                value={draft}
                                onChange={(e) => setAmountDrafts((d) => ({ ...d, [t.id]: e.target.value }))}
                                disabled={busy}
                                aria-label={`Partial refund amount for charge on ${new Date(t.created).toLocaleDateString()}`}
                              />
                              <button
                                type="button"
                                className="btn sm"
                                disabled={busy || !draftValid}
                                onClick={() => draftCents && refund(t, draftCents, formatMoney(draftCents, t.currency))}
                              >
                                {busy ? <span className="spin" /> : null}
                                Partial
                              </button>
                              <button
                                type="button"
                                className="btn danger sm"
                                disabled={busy}
                                onClick={() => refund(t, undefined, formatMoney(remaining, t.currency))}
                              >
                                {busy ? <span className="spin" /> : null}
                                Refund {formatMoney(remaining, t.currency)}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- Stripe */

function StripeTab({ push }: { push: (kind: 'ok' | 'error' | 'info' | 'warn', message: string) => void }) {
  const [settings, setSettings] = useState<StripeAdminSettings | null>(null);
  const [form, setForm] = useState({ secretKey: '', webhookSecret: '', priceMonthly: '', priceAnnual: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .admin!.stripeSettings()
      .then((s) => {
        setSettings(s);
        setForm((f) => ({ ...f, priceMonthly: s.priceMonthly, priceAnnual: s.priceAnnual }));
      })
      .catch((err) => push('error', err instanceof ApiError ? err.message : String(err)));
  }, [push]);

  async function save() {
    setSaving(true);
    try {
      const saved = await api.admin!.updateStripeSettings(form);
      setSettings(saved);
      setForm({ secretKey: '', webhookSecret: '', priceMonthly: saved.priceMonthly, priceAnnual: saved.priceAnnual });
      push('ok', 'Stripe settings saved.');
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return <div className="empty">Loading…</div>;

  return (
    <div className="form-grid">
      <p className="hint">
        {settings.configured
          ? `Configured (source: ${settings.source === 'database' ? 'this admin panel' : 'environment variables'}). Secret key ${settings.secretKey}, webhook secret ${settings.webhookSecret}.`
          : 'Billing is not configured yet — nothing will work on the Billing tab or paywall until this is set.'}
      </p>
      <div className="form-row">
        <label>Secret key</label>
        <input
          type="password"
          placeholder={settings.configured ? 'Leave blank to keep the current key' : 'sk_live_…'}
          value={form.secretKey}
          onChange={(e) => setForm((f) => ({ ...f, secretKey: e.target.value }))}
        />
      </div>
      <div className="form-row">
        <label>Webhook secret</label>
        <input
          type="password"
          placeholder={settings.configured ? 'Leave blank to keep the current secret' : 'whsec_…'}
          value={form.webhookSecret}
          onChange={(e) => setForm((f) => ({ ...f, webhookSecret: e.target.value }))}
        />
      </div>
      <div className="form-row">
        <label>Monthly price id</label>
        <input
          type="text"
          placeholder="price_…"
          value={form.priceMonthly}
          onChange={(e) => setForm((f) => ({ ...f, priceMonthly: e.target.value }))}
        />
      </div>
      <div className="form-row">
        <label>Annual price id</label>
        <input
          type="text"
          placeholder="price_…"
          value={form.priceAnnual}
          onChange={(e) => setForm((f) => ({ ...f, priceAnnual: e.target.value }))}
        />
      </div>
      <button className="btn primary" disabled={saving} onClick={save}>
        {saving ? <span className="spin" /> : null}
        Save
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------------- mail */

function MailTab({ push }: { push: (kind: 'ok' | 'error' | 'info' | 'warn', message: string) => void }) {
  const [settings, setSettings] = useState<MailAdminSettings | null>(null);
  const [form, setForm] = useState({
    host: '',
    port: 587,
    secure: false,
    user: '',
    pass: '',
    fromAddress: '',
    fromName: 'Z3NTRAFY',
  });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api
      .admin!.mailSettings()
      .then((s) => {
        setSettings(s);
        setForm({ host: s.host, port: s.port, secure: s.secure, user: s.user, pass: '', fromAddress: s.fromAddress, fromName: s.fromName });
      })
      .catch((err) => push('error', err instanceof ApiError ? err.message : String(err)));
  }, [push]);

  async function save() {
    setSaving(true);
    try {
      const saved = await api.admin!.updateMailSettings(form);
      setSettings(saved);
      setForm((f) => ({ ...f, pass: '' }));
      push('ok', 'Mail settings saved.');
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    if (!testTo.trim()) return;
    setTesting(true);
    try {
      const result = await api.admin!.testMail(testTo.trim());
      push(result.ok ? 'ok' : 'error', result.ok ? `Test email sent to ${testTo}.` : result.error ?? 'Failed to send.');
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : String(err));
    } finally {
      setTesting(false);
    }
  }

  if (!settings) return <div className="empty">Loading…</div>;

  return (
    <div className="form-grid">
      <p className="hint">
        {settings.configured
          ? `Configured (source: ${settings.source === 'database' ? 'this admin panel' : 'environment variables'}). Used for email verification, password reset, and task reminder emails.`
          : 'No mail server configured yet — verification, password reset, and reminder emails will silently not send until this is set up.'}
      </p>
      <div className="form-row">
        <label>SMTP host</label>
        <input
          type="text"
          placeholder="smtp.yourprovider.com"
          value={form.host}
          onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
        />
      </div>
      <div className="form-row">
        <label>Port</label>
        <input
          type="number"
          value={form.port}
          onChange={(e) => setForm((f) => ({ ...f, port: Number(e.target.value) || 587 }))}
        />
      </div>
      <div className="form-row">
        <label className="checkbox">
          <input type="checkbox" checked={form.secure} onChange={(e) => setForm((f) => ({ ...f, secure: e.target.checked }))} />
          Use TLS (usually on for port 465, off for 587)
        </label>
      </div>
      <div className="form-row">
        <label>SMTP username</label>
        <input type="text" value={form.user} onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))} />
      </div>
      <div className="form-row">
        <label>SMTP password</label>
        <input
          type="password"
          placeholder={settings.configured ? 'Leave blank to keep the current password' : ''}
          value={form.pass}
          onChange={(e) => setForm((f) => ({ ...f, pass: e.target.value }))}
        />
      </div>
      <div className="form-row">
        <label>From address</label>
        <input
          type="email"
          placeholder="notifications@yourdomain.com"
          value={form.fromAddress}
          onChange={(e) => setForm((f) => ({ ...f, fromAddress: e.target.value }))}
        />
      </div>
      <div className="form-row">
        <label>From name</label>
        <input type="text" value={form.fromName} onChange={(e) => setForm((f) => ({ ...f, fromName: e.target.value }))} />
      </div>
      <button className="btn primary" disabled={saving} onClick={save}>
        {saving ? <span className="spin" /> : null}
        Save
      </button>

      <div className="admin-test-mail">
        <label>Send a test email</label>
        <div className="row">
          <input type="email" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          <button className="btn ghost" disabled={testing || !testTo.trim()} onClick={sendTest}>
            {testing ? <span className="spin" /> : null}
            Send test
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- audit */

function AuditTab() {
  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null);

  useEffect(() => {
    api.admin!.auditLog().then(setEntries).catch(() => setEntries([]));
  }, []);

  if (!entries) return <div className="empty">Loading…</div>;
  if (entries.length === 0) return <div className="empty">No admin actions recorded yet.</div>;

  return (
    <ul className="audit-list">
      {entries.map((e) => (
        <li key={e.id}>
          <span className="when">{new Date(e.createdAt).toLocaleString()}</span>
          <span className="what">{e.detail ?? e.action}</span>
        </li>
      ))}
    </ul>
  );
}
